import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
	XThemeForm,
	XThemeContainer,
	XThemeDetailsCards,
	XThemeInputText,
	XThemeRadio,
	XThemeSwitchBtn,
	XThemeButton,
	XThemeTable,
	XThemeTag,
	XTag,
} from '../../../../styles/GlobalThemeStyle';
import {
	Row,
	Col,
	Button,
	Input,
	notification,
	Flex,
	Space,
	Table,
	Form,
	Tag,
	Empty,
	Tooltip,
} from 'antd';
import {
	XThemeTilesCard,
	XThemeVoiceAssCard,
	XThemeVoiceaAsistCard,
	XThemeVoiceAssSeeBtn,
	XThemeVoiceAssTable,
	XThemeVoiceAssTableCard,
	XThemeVoiceAssTitle,
	XThemeVoiceCreateCaseBtn,
	XThemeVoiceaAsistChartCard,
	XThemeVoiceaAssSearchBar,
	XThemeFitToScreen,
	XThemeFixBottom,
	XThemeVoiceaAsistChatCard,
	XThemeVSSetViewPort,
	XThemeVSScrollContainer,
} from '../../VoiceAssistanceStyle';
import { PlusCircleOutlined } from '@ant-design/icons';
import { getCasesApi, getCasesReset } from '../../../../actions/getCasesAction';
import moment from 'moment';
import { toTitleCase, truncateString } from '../../../../utils/helper';
import { useConditionalRenderer } from '../../../../utils/conditionalRenderer';

const VATable = (props) => {
	const {
		action,
		actionParameters,
		casesData,
		initialLoad,
		greeting,
		userInfo,
		getCases,
		getCasesReset,
		setProcessingAction,
		casesApiState,
		onUserInputRequired,
	} = props;

	const [tableData, setTableData] = useState([]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [displayedCount, setDisplayedCount] = useState(0);
	const [currentAction, setCurrentAction] = useState(null);
	const [currentTableFilters, setCurrentTableFilters] = useState(null);
	const navigate = useNavigate();

	// Function to handle case ID click for table_list action
	const handleCaseIdClick = (caseId) => {
		if (action === 'table_list' && onUserInputRequired) {
			// Include current table filters in the parameters
			const parameters = {
				case_id: caseId,
				process_type: 'rc',
			};
			
			// Add current table filters if they exist
			if (currentTableFilters) {
				Object.assign(parameters, currentTableFilters);
			}
			
			onUserInputRequired({
				response_type: 'action',
				action: 'case-detail',
				component: 'case_analysis',
				parameters: parameters,
			});
		}
	};

	// Create conditional renderer for table items
	const isTableListAction = action === 'table_list';
	const conditionalRenderer = useConditionalRenderer(
		isTableListAction,
		handleCaseIdClick,
		'/case/filings',
		{},
		{}
	);

	// Default filters that should be applied to search requests
	const defaultFilters = {
		search: '',
		page: 1,
		sort: 'createdAt',
		sortDirection: 'descend',
		created_on: 'all',
		hospital: '',
		organization: '',
		region: '',
		state: '',
		status: '',
		insurance_company: '',
		limit: 10,
		revenue_cycle: 'all',
		filing_status: 'all',
		for_page: 'case-filing',
		patient_name: '',
	};

	// Columns definition for the table
	const columns = [
		{
			title: 'Stg.',
			dataIndex: 'status_filing',
			width: 70,
			className: 'custom-border-left',
			render: (status_filing) => (
				<>
					<div className={`triangle ${status_filing[0]}`}></div>
					<Tooltip
						title={truncateString(
							getStatusTooltipDisplay(status_filing[1]),
						)}
					>
						<XThemeTag className="staticTag">
							{getStatusDisplay(status_filing[1])}
						</XThemeTag>
					</Tooltip>
				</>
			),
		},
		{
			title: 'Case ID',
			dataIndex: 'caseID',
			width: 120,
		},
		{
			title: 'Patient ID',
			dataIndex: 'patientID',
			width: 120,
		},
		{
			title: 'Case Name',
			dataIndex: 'caseName',
			width: 150,
		},
		{
			title: 'Insurance Company',
			dataIndex: 'insuranceCompany',
			width: '25%',
		},
		{
			title: 'Status',
			dataIndex: 'status',
			align: 'center',
			width: 120,
		},
	];

	// Initial API call when component mounts - Update this useEffect
	useEffect(() => {
		// Only make initial API call if we don't already have data
		// and initialLoad is true (meaning no URL conversationId)
		// AND there's no current action (to prevent duplicate calls)
		if ((!casesData || !casesData.all_cases) && initialLoad && !action) {
			makeApiCall(defaultFilters);
		}
	}, [initialLoad, action]);

	// Update makeApiCall function to handle more complex filter combinations
	const makeApiCall = (filters) => {
		setProcessingAction(true);
		getCases(filters);
	};

	// Enhance action handling in useEffect
	useEffect(() => {
		if (action) {
			// Store previous action before setting the new one
			const prevAction = currentAction;
			setCurrentAction(action);

			// Special case for reset_to_default - force making the default API call
			if (action === 'reset_to_default') {
				makeApiCall(defaultFilters);
				return;
			}

			// Create base filters
			let actionFilters = { ...defaultFilters };

			// Handle the new standardized table_filters action type
			if (action === 'table_filters' && actionParameters) {
				// Map all parameters directly from actionParameters to actionFilters
				// This handles all filter types in a standardized way
				Object.keys(actionParameters).forEach((key) => {
					if (
						actionParameters[key] !== null &&
						actionParameters[key] !== undefined
					) {
						actionFilters[key] = actionParameters[key];
					}
				});
			}
			// Handle table_list action - similar to table_filters but with different rendering
			else if (action === 'table_list' && actionParameters) {
				// Map all parameters directly from actionParameters to actionFilters
				// This handles all filter types in a standardized way
				Object.keys(actionParameters).forEach((key) => {
					if (
						actionParameters[key] !== null &&
						actionParameters[key] !== undefined
					) {
						actionFilters[key] = actionParameters[key];
					}
				});
			}
			// Keep legacy action handlers for backward compatibility
			// Handle revenue cycle filters
			else if (
				action === 'filter_revenue_cycle' &&
				actionParameters?.cycle_type
			) {
				actionFilters = {
					...actionFilters,
					revenue_cycle: actionParameters.cycle_type,
				};
			}
			// Handle filing status filters
			else if (
				action === 'filter_filing_status' &&
				actionParameters?.status
			) {
				actionFilters = {
					...actionFilters,
					filing_status: actionParameters.status, // escalation, at_risk, in_process, untimely
				};
			}
			// Handle combined filters (revenue cycle + filing status)
			else if (action === 'filter_combined' && actionParameters) {
				if (actionParameters.cycle_type) {
					actionFilters.revenue_cycle = actionParameters.cycle_type;
				}
				if (actionParameters.filing_status) {
					actionFilters.filing_status =
						actionParameters.filing_status;
				}
				if (actionParameters.status) {
					actionFilters.status = actionParameters.status;
				}
				if (actionParameters.hospital) {
					actionFilters.hospital = actionParameters.hospital;
				}
				if (actionParameters.state) {
					actionFilters.state = actionParameters.state;
				}
				if (actionParameters.insurance_company) {
					actionFilters.insurance_company =
						actionParameters.insurance_company;
				}
				if (actionParameters.created_on) {
					actionFilters.created_on = actionParameters.created_on;
				}

				// Add limit handling
				if (actionParameters.limit) {
					actionFilters.limit = actionParameters.limit;
				}

				// Add sort handling
				if (actionParameters.sort) {
					actionFilters.sort = actionParameters.sort;
				}

				// Add sort direction handling
				if (actionParameters.sortDirection) {
					actionFilters.sortDirection =
						actionParameters.sortDirection;
				}
			}
			// Handle search by case ID (existing functionality)
			else if (action === 'search_case' && actionParameters?.caseId) {
				actionFilters = {
					...actionFilters,
					patient_name: actionParameters.caseId,
				};
			}
			// Handle pending cases filter (existing functionality)
			else if (action === 'search_pending_cases') {
				actionFilters = {
					...actionFilters,
					status: 'pending',
				};
			}

			// Add for_page parameter for all filters
			actionFilters.for_page = 'case-filing';

			// Store the current table filters for navigation back
			setCurrentTableFilters(actionFilters);

			// Make the API call with constructed filters
			makeApiCall(actionFilters);
		} else if (currentAction) {
			// Only reset to default if we had a previous action AND initialLoad is true
			// AND we're not in the middle of switching conversations
			const isSwitchingConversations =
				document.location.pathname.includes('/agent-maxx/');

			setCurrentAction(null);
			if (initialLoad && !isSwitchingConversations) {
				makeApiCall(defaultFilters);
			}
		}
	}, [action, actionParameters]);

	// Function to get status display
	function getStatusDisplay(status) {
		if (status)
			switch (true) {
				case status?.includes('first'):
					return '1st';
				case status.includes('second'):
					return '2nd';
				case status.includes('third'):
					return '3rd';
				case status.includes('fourth'):
					return '4th';
				case status.includes('judicial'):
					return 'JR';
				case status === 'organization_determination':
					return 'OD';
				default:
					return 'OD';
			}
		return '';
	}

	// Function to get status tooltip display
	function getStatusTooltipDisplay(status) {
		if (status?.includes('first')) {
			return 'First Appeal';
		} else if (status.includes('second')) {
			return 'Second Appeal';
		} else if (status.includes('third')) {
			return 'Third Appeal';
		} else if (status.includes('fourth')) {
			return 'Fourth Appeal';
		} else if (status.includes('judicial')) {
			return 'Judicial Review';
		} else if (status === 'organization_determination') {
			return 'Organization Determination';
		} else {
			return 'Organization Determination';
		}
	}

	// Process API response data for the table
	useEffect(() => {
		if (casesData) {
			if (casesData.all_cases && casesData.all_cases.length > 0) {
				const updatedData = [];
				setTotalRecords(
					casesData.total_count || casesData.all_cases.length,
				);
				setDisplayedCount(casesData.all_cases.length);

				casesData.all_cases.forEach((item, index) => {
					// Determine if we should make columns clickable based on action
					const isTableListAction = action === 'table_list';
					
					updatedData.push({
						key: item._id,
						status_filing: [
							item.filing_status,
							item.revenue_cycle_stage,
						],
						caseID: conditionalRenderer(
							item.case_id,
							item._id,
							{ width: '100px' }
						),
						patientID: conditionalRenderer(
							item.mrn,
							item._id,
							{ width: '100px' }
						),
						caseName: conditionalRenderer(
							truncateString(item.patient_name, 25),
							item._id,
							{}
						),
						insuranceCompany: item.insurance
							? item.insurance.name
							: '-',
						status: (
							<XThemeTag className={item.status}>
								{toTitleCase(item.status)}
							</XThemeTag>
						),
						statusValue: item.status,
					});
				});

				setTableData(updatedData);
			} else {
				setTableData([]);
				setTotalRecords(0);
				setDisplayedCount(0);
			}
		}
	}, [casesData]);

	// Update getResultMessage to handle more result types
	const getResultMessage = () => {
		if (!casesData) return '';

		// Handle the new standardized table_filters action
		if (action === 'table_filters' || action === 'table_list') {
			// Build a descriptive message based on all filters
			let filterDescription = [];

			// Add entity descriptions if present
			if (actionParameters?.hospital) {
				filterDescription.push(
					`hospital: ${actionParameters.hospital}`,
				);
			}
			if (actionParameters?.organization) {
				filterDescription.push(
					`organization: ${actionParameters.organization}`,
				);
			}
			if (actionParameters?.region) {
				filterDescription.push(`region: ${actionParameters.region}`);
			}
			if (actionParameters?.state) {
				filterDescription.push(`state: ${actionParameters.state}`);
			}
			if (actionParameters?.insurance_company) {
				filterDescription.push(
					`insurance: ${actionParameters.insurance_company}`,
				);
			}

			// Other filter parameters
			if (
				actionParameters?.revenue_cycle &&
				actionParameters.revenue_cycle !== 'all'
			) {
				filterDescription.push(
					`revenue cycle: ${actionParameters.revenue_cycle.replace(
						/_/g,
						' ',
					)}`,
				);
			}
			if (
				actionParameters?.filing_status &&
				actionParameters.filing_status !== 'all'
			) {
				filterDescription.push(
					`filing status: ${actionParameters.filing_status.replace(
						/_/g,
						' ',
					)}`,
				);
			}
			if (actionParameters?.status && actionParameters.status !== 'all') {
				filterDescription.push(`status: ${actionParameters.status}`);
			}
			if (
				actionParameters?.created_on &&
				actionParameters.created_on !== 'all'
			) {
				filterDescription.push(
					`created: ${actionParameters.created_on.replace(
						/_/g,
						' ',
					)}`,
				);
			}
			if (actionParameters?.patient_name) {
				filterDescription.push(
					`search: "${actionParameters.patient_name}"`,
				);
			}

			const filterText =
				filterDescription.length > 0
					? ` with ${filterDescription.join(', ')}`
					: '';

			if (displayedCount > 0) {
				return `I found ${displayedCount} case(s)${filterText}`;
			} else {
				return `No cases found${filterText}`;
			}
		}
		// Keep existing action handlers for backward compatibility
		else if (
			action === 'filter_revenue_cycle' &&
			actionParameters?.cycle_type
		) {
			const cycleType = actionParameters.cycle_type.replace('_', ' ');
			return `I found ${displayedCount} case(s) in the ${toTitleCase(
				cycleType,
			)} category`;
		} else if (
			action === 'filter_filing_status' &&
			actionParameters?.status
		) {
			const statusType = actionParameters.status.replace('_', ' ');
			return `I found ${displayedCount} case(s) with ${toTitleCase(
				statusType,
			)} status`;
		} else if (action === 'filter_combined') {
			// Build a descriptive message based on combined filters
			let filterDescription = [];
			if (actionParameters?.cycle_type) {
				filterDescription.push(
					`revenue cycle: ${actionParameters.cycle_type.replace(
						'_',
						' ',
					)}`,
				);
			}
			if (actionParameters?.filing_status) {
				filterDescription.push(
					`filing status: ${actionParameters.filing_status.replace(
						'_',
						' ',
					)}`,
				);
			}
			if (actionParameters?.status) {
				filterDescription.push(`status: ${actionParameters.status}`);
			}
			if (actionParameters?.hospital) {
				filterDescription.push(
					`hospital: ${actionParameters.hospital}`,
				);
			}
			if (actionParameters?.state) {
				filterDescription.push(`state: ${actionParameters.state}`);
			}

			const filterText =
				filterDescription.length > 0
					? ` with ${filterDescription.join(', ')}`
					: '';

			return `I found ${displayedCount} case(s)${filterText}`;
		} else if (action === 'search_case' && actionParameters?.caseId) {
			if (displayedCount > 0) {
				return `I found ${displayedCount} result(s) for ${actionParameters.caseId}`;
			} else {
				return `No results found for ${actionParameters.caseId}`;
			}
		} else if (action === 'search_pending_cases') {
			if (displayedCount > 0) {
				return `I found ${displayedCount} pending case(s)`;
			} else {
				return 'No pending cases found';
			}
		} else {
			if (displayedCount > 0) {
				return `${greeting}, ${userInfo.name}! Here are your ${displayedCount} most recent cases.`;
			} else {
				return `${greeting}, ${userInfo.name}! You don't have any recent cases.`;
			}
		}
	};

	return (
		<>
			<>
				<>
					<Row gutter={[15, 15]}>
						{/* <Col xs={24} className="mb-3">
							<XThemeVoiceAssTitle>
								{getResultMessage()}
							</XThemeVoiceAssTitle>
						</Col> */}
						<Col xs={24}>
							<XThemeVoiceAssTableCard>
								<div className="hasPadding">
									<Row
										gutter={[15, 15]}
										className="align-items-center"
									>
										<Col xs={24} sm={12} lg={8}>
											<div className="tableTitle">
												{(action === 'table_filters' || action === 'table_list') &&
												actionParameters?.status ===
													'pending'
													? 'Pending Cases'
													: (action === 'table_filters' || action === 'table_list') &&
													  actionParameters?.patient_name
													? `Results for "${actionParameters.patient_name}"`
													: action ===
													  'search_pending_cases'
													? 'Pending Cases'
													: action ===
															'search_case' &&
													  actionParameters?.caseId
													? `Results for "${actionParameters.caseId}"`
													: 'Recent Cases'}
											</div>
										</Col>
										{/* Commented out as requested */}
										{/* <Col
											xs={24}
											sm={12}
											lg={16}
											className="text-end"
										>
											<Space>
												<XThemeInputText
													className="largeInputForms"
													size="large"
													placeholder="Search by name"
												/>
												<XThemeVoiceAssSeeBtn
													icon={
														<img
															src="/images/theme-filter-icon.svg"
															alt="filter"
														/>
													}
													size="large"
												>
													Filter
												</XThemeVoiceAssSeeBtn>
												<XThemeVoiceAssSeeBtn size="large">
													View All
												</XThemeVoiceAssSeeBtn>
											</Space>
										</Col> */}
									</Row>
								</div>
								<Row gutter={[15, 15]}>
									<Col xs={24}>
										{tableData.length > 0 ? (
											<XThemeVoiceAssTable
												loading={
													casesApiState === 'loading'
												}
												pagination={false}
												columns={columns}
												dataSource={tableData}
											/>
										) : (
											<div style={{ padding: '40px 0' }}>
												<Empty
													description={
														(action === 'table_filters' || action === 'table_list') &&
														actionParameters?.patient_name
															? `No results found for: ${actionParameters.patient_name}`
															: (action === 'table_filters' || action === 'table_list') &&
															  actionParameters?.status ===
																	'pending'
															? 'No pending cases found'
															: action ===
																	'search_case' &&
															  actionParameters?.caseId
															? `No results found for Case ID: ${actionParameters.caseId}`
															: action ===
															  'search_pending_cases'
															? 'No pending cases found'
															: 'No cases found'
													}
												/>
											</div>
										)}
									</Col>
								</Row>
							</XThemeVoiceAssTableCard>
						</Col>
					</Row>
				</>
			</>
		</>
	);
};

const mapStateToProps = (state) => {
	return {
		getCasesState: state.getCases.apiState,
		casesApiState: state.getCases.apiState,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		getCases: (data) => dispatch(getCasesApi(data)),
		getCasesReset: () => dispatch(getCasesReset()),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(VATable);
